import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { MenuSideBar } from './components/menu-side-bar/menu-side-bar';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { environment } from '../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import translationsEN from '../i18n/en.json';
import translationsDE from '../i18n/de.json';
import { AppService } from './services/app.service';
import { HTTP } from './services/http';
import { DomSanitizer } from '@angular/platform-browser';
import { registerIcons } from './icon';
import { MatIconRegistry } from '@angular/material/icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { Notification } from './interfaces';
import { TooltipModule } from './components/tooltip/tooltip';

export const DEFAULT_LANG = 'de';

const authConfig: AuthConfig = {
  issuer: `${environment.authUrl}/application/o/tickets/`,
  redirectUri: window.location.origin,
  postLogoutRedirectUri: window.location.origin,
  clientId: 'BeY6301Ub6xcWOMQgMvE4KB476rzcckLGx9FNBoi',
  responseType: 'code',
  // `features` gehoert dazu: AuthService.hasFeature liest die Flags aus dem `features`-Claim, und
  // ein authentik-Scope-Mapping liefert seinen Claim nur, wenn der Scope auch angefordert wird -
  // ohne den Scope bleibt der Claim leer und jedes Frontend-Feature unsichtbar, egal was in den
  // Gruppen-/Benutzerattributen steht. Analog zu `entitlements`.
  scope: 'openid profile email entitlements features offline_access',
  requireHttps: environment.production ? window.location.protocol === 'https:' : false,
  strictDiscoveryDocumentValidation: false,
};

@Component({
  selector: 'app-root',
  standalone: true,
  styleUrls: ['./app.component.sass'],
  imports: [CommonModule, MenuSideBar, RouterOutlet, TooltipModule],
  template: `
    @if (bare()) {
      <div class="bare-outlet"><router-outlet></router-outlet></div>
    } @else {
      <main class="app-container" [ngClass]="{'logging-in': !loggedIn}">
        @if (loggedIn) {
        <div class="menu-side-bar">
            <menu-side-bar></menu-side-bar>
          </div>
        }
      <div class="router-outlet">
          <router-outlet></router-outlet>
        </div>
        <v-tooltip></v-tooltip>
      </main>
    }
  `
})
export class AppComponent implements OnInit {
  private oauth = inject(OAuthService);
  private app = inject(AppService);
  private route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // Öffentliche Seiten ohne App-Hülle: keine Sidebar, kein Ticketsystem, kein Login.
  // Wer einen Bogen ausfüllt, ist Gast und soll nur den Bogen sehen.
  public readonly bare = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.isBarePath(this.router.url.split('?')[0])),
    ),
    { initialValue: this.isBarePath(window.location.pathname) },
  );

  private isBarePath(p: string): boolean {
    // Als vollständige Segmente geprüft und nicht als Präfix: '/fb/…' fängt zwar mit einem 'f' an,
    // beginnt aber nicht mit '/f/' - genau daran hing der allgemeine Bogen-Link zuerst in der
    // Sidebar. Ein Segment-Vergleich kann diesen Fehler nicht wiederholen.
    //   /f/<token>   ein Empfänger, persönlicher Link aus der Mail
    //   /fb/<token>  ein Link für alle, aus dem Bogen kopiert
    //   /s/<token>   Self-Service-Portal und dessen Bestätigungsseite
    const segment = p.split('/')[1] ?? '';
    return ['f', 'fb', 's'].includes(segment);
  }
  private routeData = toSignal(this.route.data,{initialValue: {notifications: []}});
  private readonly translate = inject(TranslateService)

  status = signal('init');
  accessToken = signal('');
  meResp = signal<any>(null);
  authTestResp = signal<any>(null);

  constructor(iconReg: MatIconRegistry, sanitizer: DomSanitizer) {
    registerIcons(iconReg, sanitizer);
    this.translate.setTranslation('en', translationsEN)
    this.translate.setTranslation('de', translationsDE)
    this.translate.use(localStorage.getItem('language') || DEFAULT_LANG);
    this.app.setTitle();
    (window as any).apiDebug = this.apiDebug.bind(this);
    if(window.localStorage.getItem('apiDebug') === 'true') this.apiDebug(true);
  }

  ngOnInit() {
    this.oauth.configure(authConfig);
    this.oauth.setupAutomaticSilentRefresh();
    this.oauth.loadDiscoveryDocument();
    // console.log("AppComponent initialized TODO: LOAD NOTIFICATIONS UPDATE ON RECEIVE. IF CLICKED MARK AS SEEN");
  }

  get loggedIn() { return this.oauth.hasValidAccessToken(); }

  private apiDebug(bool: boolean = true) {
    window.localStorage.setItem('apiDebug', bool ? 'true' : 'false');
    HTTP.setDebug(bool);
    console.log(`API debug mode ${bool ? 'enabled' : 'disabled'}`);
  }
}